/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { Alert, Button, Card, SvgIcon, Table } from "core/components";
import {
  material_request_items_admin_material_request_path,
  admin_material_requests_path,
  release_admin_material_request_path,
  print_issuance_admin_material_request_path
} from "routes";
import { navigateTo } from "core/navigation";
import { api, ApiError } from "core/api";
import { MaterialRequestStatus } from "core/schema";

const MaterialRequest = ({ material_request }): JSX.Element => {
  const [loading, setLoading] = React.useState(false);
  const [materialRequestItems, setMaterialRequestItems] = React.useState([]);
  const [showReleaseConfirmationModal, setShowReleaseConfirmationModal] = React.useState(false);
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [modalErrors, setModalErrors] = React.useState<Array<string>>([]);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const table = React.useRef(null);
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Inventory ID",
        accessor: "item.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Inventory",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      }
    ],
    []
  );

  const getMaterialRequestItems = async () => {
    try {
      setLoading(true);
      const response = await api.get(material_request_items_admin_material_request_path(material_request.id));
      setMaterialRequestItems(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
    }
  };

  const releaseMaterialRequest = async (id) => {
    try {
      setLoading(true);
      await api.put(release_admin_material_request_path(id));
      window.open(print_issuance_admin_material_request_path(material_request));
      navigateTo(admin_material_requests_path());
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.all().find((e) => e.includes("already closed") || e.includes("is not open"))) {
          setModalErrors(error.all());
          setShowErrorModal(!showErrorModal);
        } else setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
      setShowReleaseConfirmationModal(!showReleaseConfirmationModal);
    }
  };

  React.useEffect(() => {
    getMaterialRequestItems().catch(console.error);
  }, []);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Delivery History...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No material request items.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={materialRequestItems} ref={table} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return materialRequestItems.length > 0 ? <>{dataTable()}</> : noContent();
  };

  const canReleaseMaterialRequest = (request) => {
    return request.status == MaterialRequestStatus.Open;
  };

  const canPrintMaterialIssuance = (request) => {
    return request.status == MaterialRequestStatus.Closed;
  };

  return (
    <React.StrictMode>
      {formErrors && formErrors.length != 0 && (
        <Alert>
          {formErrors.map((error, index) => {
            return <div key={index}>{error}</div>;
          })}
        </Alert>
      )}
      <Card>
        <Card.Header title="Material Request Items">
          <Button
            title="Back"
            className="button--default"
            icon="arrow-left"
            onClick={() => {
              navigateTo(admin_material_requests_path());
            }}
          />
          {canPrintMaterialIssuance(material_request) && (
            <Button
              className="button--primary ml-3"
              icon="printer"
              title="Print"
              type="button"
              onClick={() => window.open(print_issuance_admin_material_request_path(material_request))}
            />
          )}
          {canReleaseMaterialRequest(material_request) && (
            <Button
              title="Release"
              className="button--brand ml-3"
              icon="check-circle"
              onClick={() => {
                setShowReleaseConfirmationModal(!showReleaseConfirmationModal);
              }}
            />
          )}
        </Card.Header>
        <Card.Body>{loading ? loader() : content()}</Card.Body>
      </Card>
      <Modal dialogClassName="modal-dialog-centered" show={showReleaseConfirmationModal}>
        <Modal.Header>{`Release Material Request: ${material_request?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to release Material Request to Project: ${material_request?.project?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowReleaseConfirmationModal(!showReleaseConfirmationModal);
            }}
            disabled={loading}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--success"
            icon="save"
            title="Yes, release material request."
            disabled={loading}
            onClick={() => {
              releaseMaterialRequest(material_request?.id).catch(console.error);
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showErrorModal}>
        <Modal.Header>Error encountered</Modal.Header>
        <Modal.Body>
          {modalErrors && modalErrors.length != 0 && (
            <Alert>
              {modalErrors.map((error, index) => {
                return <div key={index}>{error}</div>;
              })}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowErrorModal(!showErrorModal)} icon="times" title="Close" />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default MaterialRequest;
